import React, { useState } from 'react';
import { Formik, Form, Field, FormikHelpers } from 'formik';
import * as Yup from 'yup';
import {
  TextField,
  Button,
  Box,
  Typography,
  CssBaseline,
  Container,
  Card,
  InputAdornment,
  IconButton,
  FormHelperText,
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { LogoBanner } from '../../../icons';
import toast from 'react-hot-toast';
import { FRONTEND_URL } from '../../../config/env';
import { acceptInvitation } from '../../../api/authService';
import { useLocation } from 'react-router-dom';
import PasswordRequirements from '../../../components/PasswordRequirements';
import { newPasswordYupString } from '../../../utilities/passwordYup';
import { useAuth } from '../../../contexts/AuthContext';

interface FormValues {
  email: string;
  password: string;
  confirmPassword: string;
}

const getValidationSchema = (isSSO: boolean) =>
  Yup.object().shape({
    email: isSSO
      ? Yup.string().email('Invalid email address').required('Email is required')
      : Yup.string(),
    password: isSSO
      ? Yup.string()
      : newPasswordYupString('Password is required'),
    confirmPassword: isSSO
      ? Yup.string()
      : Yup.string()
          .oneOf([Yup.ref('password')], 'Passwords must match')
          .required('Please confirm your password'),
  })

interface InviteAcceptPageProps {
  isSSO?: boolean;
}

const InviteAcceptPage: React.FC<InviteAcceptPageProps> = ({
  isSSO = false,
}) => {
  const { ssoLogin } = useAuth();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const location = useLocation();

  // Helper to get token from query string
  const getTokenFromQuery = () => {
    const params = new URLSearchParams(location.search);
    return params.get('token') || '';
  };

  const handleSubmit = async (
    values: FormValues,
    { setSubmitting }: FormikHelpers<FormValues>
  ) => {
    try {
      if (isSSO) {
        await ssoLogin(values.email);
      } else {
        const token = getTokenFromQuery();
        if (!token) {
          toast.error('Missing or invalid invitation token.');
          setSubmitting(false);
          return;
        }
        const result = await acceptInvitation(token, values.password);
        if (result.isSuccess) {
          toast.success('Account created successfully!');
          setTimeout(() => {
            window.location.assign(`${FRONTEND_URL}/auth/login`);
          }, 1500);
        } else {
          toast.error(result.message || 'Failed to accept invitation.');
        }
      }
    } catch (error) {
      toast.error('Failed to accept invitation.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Container component="main" maxWidth="xs">
      <CssBaseline />
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
        }}
      >
        <Card sx={{ mt: 3, width: '100%', p: 3, boxShadow: 3 }}>
          <Typography component="h1" variant="h5" textAlign="center">
            Accept Invitation
          </Typography>
          <Box display="flex" justifyContent="center">
            <Box
              component="img"
              src={LogoBanner}
              alt="MarketSmart logo"
              sx={{ height: 50, mt: 2, mb: 4 }}
            />
          </Box>
          <Typography variant="body2" textAlign="center" sx={{ mb: 2 }}>
            {isSSO 
              ? 'Your organization uses SSO, enter your email to complete your account and log in.' 
              : 'Create your password to complete your account setup.'
            }
          </Typography>
          <Formik
            initialValues={{ email: '', password: '', confirmPassword: '' }}
            validationSchema={getValidationSchema(isSSO)}
            onSubmit={handleSubmit}
          >
            {({ errors, touched, isSubmitting, handleChange, handleBlur, values }) => (
              <Form>
                { isSSO ? (
                  <Field as={TextField}
                    margin="normal"
                    required
                    fullWidth
                    id="email"
                    label="Email Address"
                    name="email"
                    autoComplete="email"
                    autoFocus
                    error={touched.email && Boolean(errors.email)}
                    helperText={touched.email && (typeof errors.email === "string" ? errors.email : JSON.stringify(errors.email))}
                    onChange={handleChange}
                    onBlur={handleBlur}
                  />
                ) : (
                  <>
                    <Box sx={{ width: '100%' }}>
                      <Field
                        as={TextField}
                        margin="normal"
                        fullWidth
                        required
                        name="password"
                        label="Password"
                        type={showPassword ? 'text' : 'password'}
                        id="password"
                        autoComplete="new-password"
                        error={touched.password && Boolean(errors.password)}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        slotProps={{
                          input: {
                            endAdornment: (
                              <InputAdornment position="end">
                                <IconButton
                                  onClick={() => setShowPassword(!showPassword)}
                                  edge="end"
                                >
                                  {showPassword ? <Visibility /> : <VisibilityOff />}
                                </IconButton>
                              </InputAdornment>
                            ),
                          },
                        }}
                      />
                      <PasswordRequirements password={values.password} sx={{ mt: 0.5 }} />
                      {touched.password && errors.password ? (
                        <FormHelperText error sx={{ mx: 1.75, mt: 0.5 }}>
                          {errors.password}
                        </FormHelperText>
                      ) : null}
                    </Box>
                    <Field
                      as={TextField}
                      margin="normal"
                      fullWidth
                      required
                      name="confirmPassword"
                      label="Confirm Password"
                      type={showConfirmPassword ? 'text' : 'password'}
                      id="confirmPassword"
                      autoComplete="new-password"
                      error={
                        touched.confirmPassword && Boolean(errors.confirmPassword)
                      }
                      helperText={
                        touched.confirmPassword && errors.confirmPassword
                      }
                      onChange={handleChange}
                      onBlur={handleBlur}
                      slotProps={{
                        input: {
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton
                                onClick={() =>
                                  setShowConfirmPassword(!showConfirmPassword)
                                }
                                edge="end"
                              >
                                {showConfirmPassword ? (
                                  <Visibility />
                                ) : (
                                  <VisibilityOff />
                                )}
                              </IconButton>
                            </InputAdornment>
                          ),
                        },
                      }}
                    />
                  </>
                )}
                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  sx={{ mt: 3, mb: 2, p: 2 }}
                  disabled={isSubmitting}
                >
                  {isSSO ? 'SSO Login' : 'Create Account'}
                </Button>
              </Form>
            )}
          </Formik>
        </Card>
      </Box>
    </Container>
  );
};

export default InviteAcceptPage;
